import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { usePostApiOauthProviderCallback } from '@/api/generated/oauth/oauth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { jwtDecode } from 'jwt-decode';

export const OAuthCallbackPage: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutateAsync } = usePostApiOauthProviderCallback();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const code = searchParams.get('code');
    const isGuestFlow = !useAuthStore.getState().isAuthenticated;
    
    if (!provider) {
      toast.error('Provedor OAuth inválido');
      navigate(isGuestFlow ? '/login' : '/integrations', { replace: true });
      return;
    }

    if (!code) {
      toast.error('Código de autorização ausente da URL');
      navigate(isGuestFlow ? '/login' : '/integrations', { replace: true });
      return;
    }

    const connectStore = async () => {
      try {
        const activeWorkspaceId = useWorkspaceStore.getState().activeWorkspaceId;
        const requestData: { code: string; workspaceId?: string | null } = {
          code,
          workspaceId: !isGuestFlow && activeWorkspaceId ? activeWorkspaceId : undefined,
        };

        const response = (await mutateAsync({ provider, data: requestData })) as any;
        
        // Extract token supporting both camelCase and PascalCase
        const token =
          response?.token ||
          response?.Token ||
          response?.jwtToken ||
          response?.data?.token ||
          response?.data?.Token;
        
        if (token) {
          let userProfile = {
            id: 'usr_' + Date.now(),
            email: 'merchant@store.com',
            fullName: 'Lojista',
          };

          try {
            const decoded = jwtDecode<any>(token);
            const emailClaim =
              decoded?.email ||
              decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'];
            const nameClaim =
              decoded?.name ||
              decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
              decoded?.unique_name;
            const idClaim =
              decoded?.sub ||
              decoded?.nameid ||
              decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
              decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'];
            
            userProfile = {
              id: idClaim || 'usr_' + Date.now(),
              email: emailClaim || 'merchant@store.com',
              fullName: nameClaim || (emailClaim ? emailClaim.split('@')[0] : 'Lojista'),
            };
          } catch {
            // Keep default fallback profile if jwtDecode fails
          }

          // Persist authentication in Zustand and localStorage
          useAuthStore.getState().login(token, userProfile);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userProfile));
          }
          
          toast.success(`Login realizado com sucesso! Bem-vindo, ${userProfile.fullName}!`);
          navigate('/conversations', { replace: true });
          return;
        }

        // If no token was returned:
        if (useAuthStore.getState().isAuthenticated) {
          // Scenario A: Already authenticated user adding a store integration
          toast.success(`Loja ${provider} conectada com sucesso!`);
          navigate('/integrations', { replace: true });
        } else {
          // Scenario B: User was attempting login/SSO but backend did not issue a token
          toast.error('Não foi possível concluir o login com a Nuvemshop. Por favor, tente novamente ou entre com suas credenciais.');
          navigate('/login', { replace: true });
        }
      } catch (err: any) {
        const errorCode = err.response?.data?.code || err.response?.data?.title;
        if (errorCode === 'Identity.EmailExistsRequiresLogin') {
          navigate('/login?emailExists=true', { replace: true });
          return;
        }

        let msg = err.response?.data?.description || err.response?.data?.message || err.message;
        if (errorCode === 'Store.Forbidden') {
          msg = 'Apenas Proprietários e Administradores podem conectar lojas a este workspace.';
        } else if (errorCode === 'Store.AlreadyConnected') {
          msg = 'Esta loja já está conectada a um workspace existente.';
        } else if (errorCode === 'Store.WorkspaceRequired') {
          msg = 'Selecione um workspace antes de conectar uma loja.';
        } else if (!msg) {
          msg = `Falha ao conectar ${provider}`;
        }

        toast.error(msg);
        navigate(isGuestFlow ? '/login' : '/integrations', { replace: true });
      }
    };

    connectStore();
  }, [provider, searchParams, navigate, mutateAsync]);

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[100vh] space-y-4">
      <LoadingSpinner size="lg" />
      <p className="text-muted-foreground animate-pulse text-sm">
        Conectando sua loja {provider}...
      </p>
    </div>
  );
};

export default OAuthCallbackPage;
